import * as React from "react";
import { Table } from "antd";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { Currency } from "swan-public-api-client";
import { PaginationConfig } from "antd/lib/pagination";
import { ColumnProps, TableLocale } from "antd/lib/table";
import { AppState } from "../Store/RootReducer";
import { considerationRenderer, priceWithCurrencyViaRecordRenderer, dateTimeRenderer, priceRenderer } from "../Grid/Renderers";
import { useSelector } from "react-redux";
import { getConsiderationSorter, getReferenceSorter, getDateSorter, getNumberSorter } from "../Grid/Sorters";
import { FxConvertedTrade } from "./FxConvertedTrade";

interface PropsFromState {
  baseCurrency: Currency;
  currencies: Map<number, Currency>;
}

interface TradesGridProps {
  columns: Column[];
  bordered?: boolean;
  locale?: TableLocale;
  pagination?: PaginationConfig | false;
  title?: boolean | ((currentPageData: object[]) => React.ReactNode);
}

export type Column = "reference" | "bookingDate" | "quantity" | "marketName" | "bookingPrice" | "currentPrice" | "profitAndLoss" | "margin" | "baseCurrencyConsideration" | "deliveryCurrencyConsideration";

export const DefaultColumns: Column[] = ["reference", "bookingDate", "quantity", "marketName", "bookingPrice", "currentPrice", "profitAndLoss", "margin"];
export const DefaultSpotFxColumns: Column[] = ["reference", "baseCurrencyConsideration", "deliveryCurrencyConsideration", "bookingPrice"];

interface Props extends WrappedComponentProps, TradesGridProps {
  trades: FxConvertedTrade[];
}

function TradeGridCmp(props: Props) {
  const { baseCurrency, currencies } = useSelector<AppState, PropsFromState>(state => ({
    baseCurrency: state.clientAccount.account!.Currency,
    currencies: new Map<number, Currency>(
      state.activeTradingAccount.account!.Currencies.map(x => [parseInt(x.Key, 10), x])
    )
  }));

  const columns: Array<ColumnProps<FxConvertedTrade>> = props.columns.map(column => {
    switch (column) {
      case "reference":
        return {
          dataIndex: "Reference",
          title: props.intl.formatMessage({ id: "tradeGrid.reference" }),
          sorter: getReferenceSorter(x => x.Reference),
          sortDirections: ["ascend", "descend"],
          defaultSortOrder: "ascend"
        };
      case "bookingDate":
        return {
          dataIndex: "BookingTime",
          title: props.intl.formatMessage({ id: "tradeGrid.bookingDate" }),
          render: dateTimeRenderer(x => x.BookingTime),
          sorter: getDateSorter(x => x.BookingTime),
          sortDirections: ["ascend", "descend"],
          defaultSortOrder: "ascend"
        };
      case "quantity":
        return {
          dataIndex: "Quantity",
          title: props.intl.formatMessage({ id: "tradeGrid.quantity" }),
          render: priceWithCurrencyViaRecordRenderer(x => x.Quantity, x => x.CurrencyId, currencies),
          sorter: (a, b) => a.Quantity < b.Quantity ? -1 : 1,
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      case "marketName":
        return {
          dataIndex: "MarketName",
          title: props.intl.formatMessage({ id: "tradeGrid.marketName" }),
          sortDirections: ["ascend", "descend"]
        };
      case "bookingPrice":
        return {
          dataIndex: "BookingPrice",
          title: props.intl.formatMessage({ id: "tradeGrid.bookingPrice" }),
          render: priceWithCurrencyViaRecordRenderer(x => x.BookingPrice, _ => undefined, currencies),
          sorter: (a, b) => a.BookingPrice < b.BookingPrice ? -1 : 1,
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      case "currentPrice":
        return {
          dataIndex: "CurrentPrice",
          title: props.intl.formatMessage({ id: "tradeGrid.currentPrice" }),
          render: priceWithCurrencyViaRecordRenderer(x => x.CurrentPrice, _ => undefined, currencies),
          sorter: (a, b) => a.CurrentPrice < b.CurrentPrice ? -1 : 1,
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      case "profitAndLoss":
        return {
          dataIndex: "BaseCurrencyOpenPnL",
          title: props.intl.formatMessage({ id: "tradeGrid.baseCurrencyProfitAndLoss" }, { symbol: baseCurrency.Symbol }),
          render: priceRenderer(x => x.BaseCurrencyOpenPnL, baseCurrency),
          sorter: getNumberSorter(x => x.BaseCurrencyOpenPnL),
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      case "margin":
        return {
          dataIndex: "BaseCurrencyMarginRequirement",
          title: props.intl.formatMessage({ id: "tradeGrid.baseCurrencyMargin" }, { symbol: baseCurrency.Symbol }),
          render: priceRenderer(x => x.BaseCurrencyMarginRequirement, baseCurrency),
          sorter: getNumberSorter(x => x.BaseCurrencyMarginRequirement),
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      case "baseCurrencyConsideration":
        return {
          dataIndex: "Consideration.BaseConsideration.Value",
          title: props.intl.formatMessage({ id: "tradeGrid.baseCurrencyConsideration" }),
          render: considerationRenderer(
            x => x.Consideration.BaseConsideration,
            currencies
          ),
          sorter: getConsiderationSorter(x => x.Consideration.BaseConsideration),
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      case "deliveryCurrencyConsideration":
        return {
          dataIndex: "Consideration.DeliveryConsideration.Value",
          title: props.intl.formatMessage({ id: "tradeGrid.deliveryCurrencyConsideration" }),
          render: considerationRenderer(
            x => x.Consideration.DeliveryConsideration,
            currencies
          ),
          sorter: getConsiderationSorter(x => x.Consideration.DeliveryConsideration),
          sortDirections: ["ascend", "descend"],
          align: "right"
        };
      default:
        throw Error(`Unknown column "${column}"`);
    }
  });

  return (
    <Table
      dataSource={props.trades}
      rowKey={(record: FxConvertedTrade) => record.InstructionKey.Key}
      columns={columns}
      size="middle"
      bordered={props.bordered}
      title={props.title === undefined || props.title === false
        ? undefined
        : props.title === true
          ? () => props.intl.formatMessage({ id: "tradeGrid.title" })
          : props.title}
      locale={props.locale}
      pagination={props.pagination}
    />
  );
}

export const TradesGrid = injectIntl(TradeGridCmp);
